/**
 * The paper header - what prints at the top of the page.
 *
 * Called "Paper details" once, which read as though it were another question and
 * left teachers unsure where the header ended and the questions began. It is now
 * named after the thing it produces, and set apart from the question list.
 *
 * Section headings moved out of here when a paper gained more than one section:
 * they belong beside the questions they introduce, not in the header block.
 *
 * Collapsed by default once the details are filled in - a teacher adding question
 * fourteen does not need the school name on screen - and opened automatically when
 * something is missing, because papers used to export with blank Class and Subject
 * lines and nobody could see why.
 */

import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { api } from '../../lib/api/client'
import type { SheetType, WorksheetDetail } from '../../lib/api/client'
import { Button, Checkbox, Dialog, Field, Input, Select, Textarea } from '../../components/ui'
import { isExamSheet, SHEET_TYPES, sheetTypeLabel } from '../../lib/sheetTypes'

/** Shared with the toolbar, which opens this block to rename the paper. */
export const PAPER_HEADER_ID = 'tc-paper-header'
export const PAPER_TITLE_ID = 'tc-paper-title'

/** Open the paper header and put the cursor in the title, ready to overtype. */
export function focusPaperTitle() {
  const block = document.getElementById(PAPER_HEADER_ID)
  if (block instanceof HTMLDetailsElement) block.open = true
  const field = document.getElementById(PAPER_TITLE_ID)
  if (field instanceof HTMLInputElement) {
    field.scrollIntoView({ block: 'nearest' })
    field.focus()
    field.select()
  }
}

export function PaperDetails({
  detail,
  editable,
  onSaved,
  onSaving,
  onFailed,
}: {
  detail: WorksheetDetail
  editable: boolean
  onSaved: () => void
  onSaving: () => void
  onFailed: (error: Error) => void
}) {
  const headerValues = (detail.header_values ?? {}) as Record<string, string>
  const [open, setOpen] = useState(Object.keys(headerValues).length === 0)
  const [title, setTitle] = useState(detail.title)
  const [titleSize, setTitleSize] = useState(detail.title_size)
  const [values, setValues] = useState(headerValues)
  const [copies, setCopies] = useState(detail.print_run.copies?.toString() ?? '')
  const [neededBy, setNeededBy] = useState(detail.print_run.needed_by ?? '')
  const [printNotes, setPrintNotes] = useState(detail.print_run.notes ?? '')
  const [pendingType, setPendingType] = useState<SheetType | null>(null)
  const templates = useQuery({ queryKey: ['templates'], queryFn: () => api.templates() })
  const availableTypes = SHEET_TYPES.filter(
    (type) =>
      type.value === detail.sheet_type ||
      (templates.data ?? []).some((template) => template.sheet_type === type.value),
  )

  useEffect(() => setTitle(detail.title), [detail.title])
  useEffect(() => setTitleSize(detail.title_size), [detail.title_size])
  useEffect(
    () => setValues((detail.header_values ?? {}) as Record<string, string>),
    [detail.header_values],
  )

  const run = (work: Promise<unknown>) => {
    onSaving()
    return work.then(onSaved).catch(onFailed)
  }

  if (!editable) return null

  return (
    <>
      <div className="tc-paper-type-bar">
        <Field
          label="Sheet type"
          hint={detail.answers_allowed ? 'Answer tools are available.' : 'Answers are hidden for exams.'}
        >
          <Select
            value={detail.sheet_type}
            onChange={(event) => setPendingType(event.target.value as SheetType)}
          >
            {availableTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="After each question"
          hint="Applied to the preview and downloaded copies."
        >
          <Select
            value={detail.question_spacing ?? 'compact'}
            onChange={(event) =>
              run(
                api.updateWorksheet(detail.id, {
                  question_spacing: event.target.value as WorksheetDetail['question_spacing'],
                }),
              )
            }
          >
            <option value="compact">Compact</option>
            <option value="one_enter">One Enter</option>
            <option value="blank_line">One blank line</option>
          </Select>
        </Field>
      </div>

      <details
        // Addressed by id so the paper's name in the toolbar can open this and
        // put the cursor in the title field. Renaming is the first thing almost
        // everyone does, and it lives three clicks in without that.
        id={PAPER_HEADER_ID}
        className="tc-details tc-header-block"
        open={open}
        onToggle={(e) => setOpen(e.currentTarget.open)}
      >
        <summary>
          Paper header
          <span className="tc-field-hint"> — what prints at the top of every page</span>
        </summary>

        <div className="tc-details-body">
          <div className="tc-details-grid">
            <Field
              label="Paper title"
              hint="The school controls its position; you can change the wording."
              id={PAPER_TITLE_ID}
            >
              <Input
                id={PAPER_TITLE_ID}
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                onBlur={() => title.trim() && run(api.updateWorksheet(detail.id, { title }))}
              />
            </Field>
            <Field label="Title size" hint="Bold in every size. The school layout stays unchanged.">
              <Select
                value={titleSize}
                onChange={(event) => {
                  const value = event.target.value as 'small' | 'standard' | 'large'
                  setTitleSize(value)
                  run(api.updateWorksheet(detail.id, { title_size: value }))
                }}
              >
                <option value="small">Small</option>
                <option value="standard">Standard</option>
                <option value="large">Large</option>
              </Select>
            </Field>
          </div>

          {/* Header field labels come from the school's template, so the keys here
              are whatever that school chose to print. */}
          {Object.keys(values).length > 0 && (
            <div className="tc-details-grid">
              {Object.entries(values).map(([key, value]) => (
                <Field key={key} label={humanise(key)}>
                  <Input
                    value={value ?? ''}
                    onChange={(event) => setValues({ ...values, [key]: event.target.value })}
                    onBlur={() => run(api.updateWorksheet(detail.id, { header_values: values }))}
                  />
                </Field>
              ))}
            </div>
          )}

          {isExamSheet(detail.sheet_type) ? (
            <div className="tc-exam-marks-note" role="note">
              <strong>Marks are included on every exam paper.</strong>
              <span>Fill the marks for each question in the question editor.</span>
            </div>
          ) : (
            <Checkbox
              label="This paper carries marks"
              checked={detail.marks_enabled}
              title="Turn on when this worksheet should show marks for every question."
              onChange={(event) =>
                run(api.updateWorksheet(detail.id, { marks_enabled: event.target.checked }))
              }
            />
          )}

          <div className="tc-details-grid">
            <Field label="Copies" hint="For the printing team.">
              <Input
                type="number"
                min={1}
                max={1000}
                value={copies}
                onChange={(event) => setCopies(event.target.value)}
                onBlur={() => {
                  const value = Number(copies)
                  if (Number.isInteger(value) && value > 0) {
                    run(api.updatePrintRun(detail.id, { copies: value, clear_needed_by: false }))
                  }
                }}
              />
            </Field>
            <Field label="Needed by">
              <Input
                type="date"
                value={neededBy}
                onChange={(event) => setNeededBy(event.target.value)}
                onBlur={() =>
                  run(
                    api.updatePrintRun(
                      detail.id,
                      neededBy
                        ? { needed_by: neededBy, clear_needed_by: false }
                        : { clear_needed_by: true },
                    ),
                  )
                }
              />
            </Field>
          </div>
          <Field label="Printing notes" hint="For example, double-sided or staple by class.">
            <Textarea
              value={printNotes}
              onChange={(event) => setPrintNotes(event.target.value)}
              onBlur={() =>
                run(api.updatePrintRun(detail.id, { notes: printNotes, clear_needed_by: false }))
              }
            />
          </Field>
        </div>
      </details>

      <Dialog
        open={pendingType !== null}
        title={`Change to ${pendingType ? sheetTypeLabel(pendingType) : 'this type'}?`}
        onClose={() => setPendingType(null)}
        footer={
          <>
            <Button onClick={() => setPendingType(null)}>Keep current type</Button>
            <Button
              variant="primary"
              onClick={() => {
                if (!pendingType) return
                run(api.updateWorksheet(detail.id, { sheet_type: pendingType })).finally(() =>
                  setPendingType(null),
                )
              }}
            >
              Change type
            </Button>
          </>
        }
      >
        <p className="tc-type-warning">
          {pendingType && isExamSheet(pendingType)
            ? 'Exam papers always include marks and never show answers. The answer preview and Teacher copy will be disabled. Your questions will stay unchanged.'
            : 'Worksheet answer tools will be available again. Your questions and marks will stay unchanged.'}
        </p>
      </Dialog>
    </>
  )
}

function humanise(key: string) {
  if (key === 'student_name') return 'Student name'
  const words = key.replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}
